import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import { FiPieChart } from 'react-icons/fi';
import PropTypes from 'prop-types';

const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && error.error) return error.error;
  return 'An error occurred';
};

const P_COLOR = '#F59E0B';
const J_COLOR = '#10B981';

const HtmlTooltip = ({ visible, x, y, type, count, pct }) => {
  if (!visible) return null;
  const color = type === 'p' ? P_COLOR : J_COLOR;
  const label = type === 'p' ? 'Number of P' : 'Number of J';
  return (
    <div
      style={{
        position: 'fixed',
        left: x + 14,
        top: y - 10,
        zIndex: 9999,
        pointerEvents: 'none',
        background: 'white',
        border: `2px solid ${color}`,
        borderRadius: '10px',
        padding: '10px 14px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.13)',
        minWidth: '140px'
      }}
    >
      <p className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-1">{label}</p>
      <p className="tw-text-2xl tw-font-extrabold" style={{ color }}>{count}</p>
      <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">{pct.toFixed(1)}% of projects</p>
    </div>
  );
};

HtmlTooltip.propTypes = {
  visible: PropTypes.bool,
  x:       PropTypes.number,
  y:       PropTypes.number,
  type:    PropTypes.oneOf(['p', 'j']),
  count:   PropTypes.number,
  pct:     PropTypes.number
};

/**
 * Proper SVG donut chart – does not deform on any ratio.
 */
const DonutPJ = ({ pCount, jCount }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, type: null });

  const total  = pCount + jCount;
  const size   = 260;
  const cx     = size / 2;
  const cy     = size / 2;
  const outerR = size / 2 - 14;
  const innerR = outerR * 0.52;

  if (total === 0) return null;

  // angles from -90 deg (top)
  const pAngle = (pCount / total) * 2 * Math.PI;
  const jAngle = (jCount / total) * 2 * Math.PI;

  const toXY = (angle, r) => ({
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle)
  });

  const startAngle = -Math.PI / 2;
  const pEnd       = startAngle + pAngle;
  const jEnd       = pEnd + jAngle;

  const pLarge = pAngle > Math.PI ? 1 : 0;
  const jLarge = jAngle > Math.PI ? 1 : 0;

  // P-Type donut arc (outer CW, inner CCW)
  const p0o = toXY(startAngle, outerR);
  const p1o = toXY(pEnd,       outerR);
  const p1i = toXY(pEnd,       innerR);
  const p0i = toXY(startAngle, innerR);
  const pPath = [
    `M ${p0o.x} ${p0o.y}`,
    `A ${outerR} ${outerR} 0 ${pLarge} 1 ${p1o.x} ${p1o.y}`,
    `L ${p1i.x} ${p1i.y}`,
    `A ${innerR} ${innerR} 0 ${pLarge} 0 ${p0i.x} ${p0i.y}`,
    'Z'
  ].join(' ');

  // J-Type donut arc
  const j0o = toXY(pEnd,  outerR);
  const j1o = toXY(jEnd,  outerR);
  const j1i = toXY(jEnd,  innerR);
  const j0i = toXY(pEnd,  innerR);
  const jPath = [
    `M ${j0o.x} ${j0o.y}`,
    `A ${outerR} ${outerR} 0 ${jLarge} 1 ${j1o.x} ${j1o.y}`,
    `L ${j1i.x} ${j1i.y}`,
    `A ${innerR} ${innerR} 0 ${jLarge} 0 ${j0i.x} ${j0i.y}`,
    'Z'
  ].join(' ');

  // Label positions
  const pMid = startAngle + pAngle / 2;
  const jMid = pEnd + jAngle / 2;
  const labelR = (outerR + innerR) / 2;
  const pLbl = toXY(pMid, labelR);
  const jLbl = toXY(jMid, labelR);

  const pHovered = tooltip.type === 'p';
  const jHovered = tooltip.type === 'j';

  return (
    <>
      <HtmlTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        type={tooltip.type}
        count={tooltip.type === 'p' ? pCount : jCount}
        pct={tooltip.type === 'p' ? (pCount / total) * 100 : (jCount / total) * 100}
      />
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="tw-w-full">
        {/* P-Type slice */}
        <path
          d={pPath}
          fill={P_COLOR}
          stroke="white"
          strokeWidth={pHovered ? 3 : 2}
          opacity={pHovered ? 1 : 0.88}
          style={{ filter: pHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none', transition: 'all 0.2s', cursor: 'pointer' }}
          onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, type: 'p' })}
          onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, type: null })}
        />
        {/* J-Type slice */}
        <path
          d={jPath}
          fill={J_COLOR}
          stroke="white"
          strokeWidth={jHovered ? 3 : 2}
          opacity={jHovered ? 1 : 0.88}
          style={{ filter: jHovered ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'none', transition: 'all 0.2s', cursor: 'pointer' }}
          onMouseMove={(e) => setTooltip({ visible: true, x: e.clientX, y: e.clientY, type: 'j' })}
          onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, type: null })}
        />

        {/* Labels inside slices */}
        {pCount > 0 && pAngle > 0.4 && (
          <text x={pLbl.x} y={pLbl.y} textAnchor="middle" dominantBaseline="middle"
            fontSize="14" fontWeight="700" fill="white" pointerEvents="none">
            {pCount}
          </text>
        )}
        {jCount > 0 && jAngle > 0.4 && (
          <text x={jLbl.x} y={jLbl.y} textAnchor="middle" dominantBaseline="middle"
            fontSize="14" fontWeight="700" fill="white" pointerEvents="none">
            {jCount}
          </text>
        )}

        {/* Center */}
        <text x={cx} y={cy - 8} textAnchor="middle" fontSize="26" fontWeight="800" fill="#111827">
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#6b7280">
          Projects
        </text>
      </svg>
    </>
  );
};

DonutPJ.propTypes = {
  pCount: PropTypes.number.isRequired,
  jCount: PropTypes.number.isRequired
};

const PJProjectsChart = ({ summary, loading, error }) => {
  if (loading) {
    return (
      <Card className="tw-p-6 tw-border tw-border-gray-200">
        <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="tw-bg-red-50 tw-border tw-border-red-200 tw-p-6">
        <p className="tw-text-red-600 tw-font-semibold">Error loading P/J projects</p>
        <p className="tw-text-red-500 tw-text-sm">{getErrorMessage(error)}</p>
      </Card>
    );
  }

  const pCount = summary?.p_type_count || 0;
  const jCount = summary?.j_type_count || 0;
  const total  = pCount + jCount;

  const pPct = total > 0 ? ((pCount / total) * 100).toFixed(1) : 0;
  const jPct = total > 0 ? ((jCount / total) * 100).toFixed(1) : 0;

  return (
    <Card className="tw-bg-gradient-to-br tw-from-green-50 tw-via-white tw-to-emerald-50 tw-border tw-border-gray-200 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-p-6 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiPieChart className="tw-text-green-600" size={20} />
          P/J Projects Overview
        </h3>
        <p className="tw-text-sm tw-text-gray-600 tw-mt-1">
          Distribution of P number and J number projects — hover slices for details
        </p>
      </div>

      <div className="tw-p-6">
        {total === 0 ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-gray-400">
            <FiPieChart size={36} className="tw-mb-2" />
            <p className="tw-text-sm">No project data available</p>
          </div>
        ) : (
          <>
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-6">
              {/* Chart */}
              <div className="tw-flex tw-justify-center tw-flex-shrink-0">
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm" style={{ width: 260 }}>
                  <DonutPJ pCount={pCount} jCount={jCount} />
                </div>
              </div>

              {/* Legend */}
              <div className="tw-flex-1 tw-space-y-4 tw-w-full">
                {/* P-Type card */}
                <div className="tw-p-4 tw-rounded-lg tw-bg-gradient-to-r tw-from-amber-50 tw-to-orange-50 tw-border tw-border-amber-200">
                  <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                    <div className="tw-w-4 tw-h-4 tw-rounded-xl tw-flex-shrink-0" style={{ backgroundColor: P_COLOR }} />
                    <span className="tw-font-semibold tw-text-amber-800 tw-text-sm">Number of P</span>
                  </div>
                  <div className="tw-flex tw-items-end tw-justify-between">
                    <p className="tw-text-3xl tw-font-extrabold tw-text-amber-900">{pCount}</p>
                    <div className="tw-text-right">
                      <p className="tw-text-lg tw-font-bold tw-text-amber-600">{pPct}%</p>
                      <p className="tw-text-xs tw-text-amber-500">of total</p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="tw-mt-3 tw-h-2 tw-bg-amber-100 tw-rounded-xl tw-overflow-hidden">
                    <div
                      className="tw-h-full tw-rounded-xl tw-transition-all"
                      style={{ width: `${pPct}%`, backgroundColor: P_COLOR }}
                    />
                  </div>
                </div>

                {/* J-Type card */}
                <div className="tw-p-4 tw-rounded-lg tw-bg-gradient-to-r tw-from-emerald-50 tw-to-green-50 tw-border tw-border-emerald-200">
                  <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                    <div className="tw-w-4 tw-h-4 tw-rounded-xl tw-flex-shrink-0" style={{ backgroundColor: J_COLOR }} />
                    <span className="tw-font-semibold tw-text-emerald-800 tw-text-sm">Number of J</span>
                  </div>
                  <div className="tw-flex tw-items-end tw-justify-between">
                    <p className="tw-text-3xl tw-font-extrabold tw-text-emerald-900">{jCount}</p>
                    <div className="tw-text-right">
                      <p className="tw-text-lg tw-font-bold tw-text-emerald-600">{jPct}%</p>
                      <p className="tw-text-xs tw-text-emerald-500">of total</p>
                    </div>
                  </div>
                  <div className="tw-mt-3 tw-h-2 tw-bg-emerald-100 tw-rounded-xl tw-overflow-hidden">
                    <div
                      className="tw-h-full tw-rounded-xl tw-transition-all"
                      style={{ width: `${jPct}%`, backgroundColor: J_COLOR }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-mt-4">
              Hover slices to see details
            </p>
          </>
        )}
      </div>
    </Card>
  );
};

PJProjectsChart.propTypes = {
  summary: PropTypes.shape({
    p_type_count:   PropTypes.number,
    j_type_count:   PropTypes.number,
    total_projects: PropTypes.number
  }),
  loading: PropTypes.bool,
  error:   PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};
PJProjectsChart.defaultProps = { summary: null, loading: false, error: null };

export default PJProjectsChart;
